import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { motion } from "framer-motion";
import { useShop, useI18n } from "@/context/Shop";
import { media } from "@/lib/api";
import { productPath } from "@/lib/nav";
import { Price } from "@/components/store/Price";

export function ProductCard({ p, index = 0, eager = false }) {
  const { isWished, toggleWishlist } = useShop();
  const { t, tp } = useI18n();
  const wished = isWished(p.id);
  const priceRange = p.max_price && p.min_price !== p.max_price;
  const name = tp(p, "name");
  const swatches = p.color_swatches || [];
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.45, delay: Math.min(index, 8) * 0.04, ease: [0.22, 1, 0.36, 1] }}
      className="group relative" data-testid="product-card">
      <Link to={productPath(p.slug)} className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/40" data-testid="product-card-link" aria-label={`${p.brand_name} ${name}`}>
        <div className="relative overflow-hidden bg-muted aspect-[3/4]">
          {p.primary_image ? (
            <>
              <img src={media(p.primary_image, 700)} alt={`${p.brand_name} ${name}`} loading={eager ? "eager" : "lazy"} className={`absolute inset-0 w-full h-full object-cover img-zoom ${p.secondary_image ? "lg:group-hover:opacity-0" : ""}`} />
              {p.secondary_image && <img src={media(p.secondary_image, 700)} alt="" loading="lazy" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-0 lg:group-hover:opacity-100 transition-opacity duration-500" />}
            </>
          ) : <div className="absolute inset-0 grid place-items-center text-xs text-muted-foreground">No image</div>}
          <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
            {!p.in_stock && <span className="bg-background/95 text-foreground text-[10px] uppercase tracking-[0.14em] px-2 py-1" data-testid="product-card-sold-out">{t("sold_out")}</span>}
            {p.in_stock && p.on_sale && p.max_discount_pct > 0 && <span className="bg-[hsl(var(--sale))] text-[hsl(var(--sale-foreground))] text-[10px] font-semibold tracking-[0.08em] px-2 py-1 tabular" data-testid="product-card-discount-badge">-{p.max_discount_pct}%</span>}
            {p.in_stock && p.is_new && !p.on_sale && <span className="bg-background/95 border border-foreground/20 text-[10px] uppercase tracking-[0.14em] px-2 py-1" data-testid="product-card-new-badge">{t("new")}</span>}
            {p.in_stock && p.low_stock && <span className="bg-background/95 border border-[hsl(var(--sale))] text-[hsl(var(--sale))] text-[10px] uppercase tracking-[0.14em] px-2 py-1" data-testid="product-card-low-stock-badge">{t("low_stock")}</span>}
          </div>
          {p.sizes?.length > 1 && (
            <div className="absolute inset-x-0 bottom-0 translate-y-full lg:group-hover:translate-y-0 transition-transform duration-300 bg-background/95 px-3 py-2 text-[11px] text-foreground/80 hidden lg:flex items-center gap-2 flex-wrap" data-testid="product-card-sizes">
              <span className="eyebrow">{t("sizes")}</span>
              {p.sizes.map((s) => <span key={s} className={`tabular ${p.in_stock_sizes?.includes(s) ? "" : "line-through opacity-40"}`}>{s}</span>)}
            </div>
          )}
        </div>
        <div className="pt-3.5 pr-8">
          <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-foreground leading-tight" data-testid="product-card-brand">{p.brand_name}</p>
          <p className="text-[13px] text-foreground/80 leading-snug mt-1 line-clamp-2" data-testid="product-card-name">{name}</p>
          <div className="mt-2 flex items-start gap-1.5">
            {priceRange && <span className="text-[12px] text-muted-foreground mt-px">{t("from")}</span>}
            <Price value={p.min_price} full={p.min_full_price} onSale={p.on_sale} size="md" testid="product-card-price" />
          </div>
          {swatches.length > 1 && (
            <div className="mt-2 flex items-center gap-1.5" aria-label={`${swatches.length} ${t("colours")}`} data-testid="product-card-swatches">
              {swatches.slice(0, 5).map((s) => <span key={s.name} title={s.name} className={`h-3 w-3 rounded-full border border-foreground/20 ${s.in_stock ? "" : "opacity-40"}`} style={{ background: s.hex || "#e5e5e5" }} />)}
              {swatches.length > 5 && <span className="text-[10px] text-muted-foreground">+{swatches.length - 5}</span>}
            </div>
          )}
        </div>
      </Link>
      <button onClick={() => toggleWishlist(p)} aria-label={wished ? t("removed_from_wishlist") : t("wishlist")} aria-pressed={wished}
        className="absolute right-2 top-2 p-2 rounded-full bg-background/85 hover:bg-background transition-colors" data-testid="product-card-wishlist-button">
        <Heart className={`h-[17px] w-[17px] ${wished ? "fill-foreground" : ""}`} strokeWidth={1.5} />
      </button>
    </motion.div>
  );
}

export function ProductGrid({ items, testid = "product-grid", eagerCount = 0 }) {
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-8 sm:gap-x-4 md:grid-cols-3 md:gap-x-6 lg:grid-cols-4 lg:gap-x-8 lg:gap-y-12" data-testid={testid}>
      {items.map((p, i) => <ProductCard key={p.id} p={p} index={i} eager={i < eagerCount} />)}
    </div>
  );
}

export function ProductRail({ title, items, link, linkLabel, testid, eyebrow }) {
  const { t } = useI18n();
  if (!items?.length) return null;
  return (
    <section className="container-lux py-12 lg:py-16" data-testid={testid}>
      <div className="flex items-end justify-between gap-6 mb-8">
        <div>
          {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-[-0.01em]">{title}</h2>
        </div>
        {link && <Link to={link} className="text-[12px] uppercase tracking-[0.14em] font-semibold link-lux whitespace-nowrap" data-testid={`${testid}-view-all`}>{linkLabel || t("view_all")}</Link>}
      </div>
      <ProductGrid items={items.slice(0, 8)} testid={`${testid}-grid`} />
    </section>
  );
}

export function SkeletonGrid({ n = 8 }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12" data-testid="skeleton-grid">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i}><div className="aspect-[3/4] bg-muted animate-pulse" /><div className="h-3 w-24 bg-muted mt-4 animate-pulse" /><div className="h-3 w-40 bg-muted mt-2 animate-pulse" /><div className="h-3 w-16 bg-muted mt-2 animate-pulse" /></div>
      ))}
    </div>
  );
}
